import { useFormatter, useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BaseLineFeedback } from '@/app/components/icons/BaseLineFeedback';
import { Copy } from '@/app/components/icons/Copy';
import { CollectBtn } from '@/app/components/shared/Community';
import { EllipsisDropdownMenu } from '@/app/components/shared/EllipsisDropdownMenu';
import LikeButton from '@/app/components/shared/LikeButton';
import { MediaPromptsDisplay } from '@/app/components/shared/MediaPromptsDisplay';
import { hasTextWithTagsContent, TextWithTags } from '@/app/components/shared/TextWithTags';
import { Button } from '@/app/components/ui/button';
import { SITE, TYPE_KEY_MAP } from '@/app/constants';
import { useCopy, useDevice, useGlobalModal } from '@/app/hooks';
import { type ActionDialogType, CommunityEnum, EllipsisActionTypeEnum } from '@/app/types';
import { cn, getReportParams } from '@/app/utils';
import { getCreationVideoAspectRatio } from '@/app/utils/client';
import type { MiniVideoInspirationInterface } from '../types';
import { getDisplayIpLocation, getTaskInputs } from '../utils';
import { CommunityShareBtn } from './CommunityShareBtn';
import { FeedDetailMediaFrame } from './FeedDetailMediaFrame';
import { UserInfo } from './UserInfo';

export const MiniVideoInspiration = ({
  miniVideo,
  onVideoPlayEnd,
  handleCollect,
  handleUnCollect,
  onClose,
  handleVote,
  handleUnVote,
  mediaAreaRef,
}: MiniVideoInspirationInterface) => {
  const t = useTranslations();
  const format = useFormatter();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const date = new Date(miniVideo?.info?.created_at ?? '');

  const { open: openActionDialog } = useGlobalModal('actionDialog');
  const { copy } = useCopy();
  const { isMobile } = useDevice();

  const taskInfo = useMemo(() => getTaskInputs(miniVideo.task), [miniVideo.task]);

  const handleActionDialog = (action: EllipsisActionTypeEnum) => {
    const reportParams = miniVideo.task?.type ? getReportParams(miniVideo.task?.type, taskInfo?.settings) : {};
    openActionDialog({
      action: action as ActionDialogType,
      id: miniVideo.info?.id || '',
      type: 'creation',
      reportParams: reportParams,
    });
  };

  const [aspectRatio, setAspectRatio] = useState<number | null>(null);

  useEffect(() => {
    const fetchVideoMetadata = async () => {
      const nextAspectRatio = await getCreationVideoAspectRatio({
        videoUrl: miniVideo.info?.creation?.uri || '',
        resolution: miniVideo.info?.creation?.size,
        shouldProbeMetadata: !isMobile,
      });
      setAspectRatio(nextAspectRatio ?? 16 / 9);
    };

    fetchVideoMetadata();
  }, [isMobile, miniVideo.info?.creation?.size, miniVideo.info?.creation?.uri]);

  /**
   * 媒体区域
   * @returns
   */
  const renderMediaArea = useCallback(() => {
    return (
      <FeedDetailMediaFrame
        src={miniVideo?.info?.creation?.uri || ''}
        poster={miniVideo.info?.creation?.cover_uri || ''}
        aspectRatio={aspectRatio}
        exploreTab={CommunityEnum.MiniVideo}
        creationId={miniVideo?.info?.id || ''}
        autoPlay={true}
        hasAudio={miniVideo.task?.creations?.[0]?.has_audio}
        mediaAreaRef={mediaAreaRef as React.RefObject<HTMLDivElement | null>}
        onPlayEnd={onVideoPlayEnd}
      />
    );
  }, [
    aspectRatio,
    mediaAreaRef,
    miniVideo.info?.creation?.cover_uri,
    miniVideo.info?.creation?.uri,
    miniVideo.info?.id,
    miniVideo.task?.creations,
    miniVideo.task?.id,
    onVideoPlayEnd,
  ]);

  /**
   * 小视频信息栏
   * @returns
   */
  const renderInfo = () => {
    return (
      <>
        <div className="flex h-full w-[304px] flex-1 flex-col overflow-y-auto md:w-full">
          {miniVideo.info?.creator && (
            <UserInfo
              user={miniVideo.info.creator}
              ipLocation={getDisplayIpLocation(miniVideo.info.creator.id, miniVideo.ip_location)}
            />
          )}

          <div className="mt-6 md:mt-5">
            <span className="font-semibold text-sm text-system-white">{t('creations.detail.title')}</span>
          </div>

          <div
            className={cn(
              'mt-5 flex flex-col gap-3 md:mt-4 md:gap-2',
              !hasTextWithTagsContent(taskInfo?.textPrompt, taskInfo?.prompts) && 'flex-initial',
            )}
          >
            {
              // 媒体区域（图片/视频/主体）
              taskInfo?.mediaPrompts && taskInfo.mediaPrompts.length > 0 && (
                <div>
                  <MediaPromptsDisplay prompts={taskInfo.mediaPrompts} taskType={taskInfo.taskType} />
                </div>
              )
            }
            {taskInfo && hasTextWithTagsContent(taskInfo.textPrompt, taskInfo.prompts) && (
              <div className="relative w-full text-sm text-system-white leading-5">
                <TextWithTags
                  text={taskInfo.textPrompt}
                  prompts={taskInfo.prompts}
                  onClick={onClose}
                  tagClassName="bg-white/10 hover:bg-system-white/20"
                />
                {taskInfo?.textPrompt && (
                  <Button size="original" variant="text" className="ml-2 translate-y-0.5">
                    <Copy
                      className="size-4 text-system-white64 hover:text-system-white"
                      onClick={() => copy(taskInfo.textPrompt)}
                    />
                  </Button>
                )}
              </div>
            )}

            {renderSettings()}

            {renderExtraInfo()}
          </div>
        </div>
        <div className="mt-4 w-full">{renderCommonOperations()}</div>
      </>
    );
  };

  /**
   * 基本的参数信息
   * @returns
   */
  const renderSettings = () => {
    if (!taskInfo?.settings)
      return (
        <div className="break-all pb-6 text-sm text-white leading-5">{t('create.createForm.settings.noOrigin')}</div>
      );

    const renderTag = (tag: string) => (
      <span className="h-6 rounded-6 bg-system-white10 px-2 py-1 text-system-white64 text-xs">{tag}</span>
    );

    return (
      <div className="flex flex-wrap items-center gap-1">
        {/* 任务类型 */}
        {renderTag(t(`create.${TYPE_KEY_MAP[taskInfo.taskType]}`))}
      </div>
    );
  };

  /**
   * 图片灵感的额外信息
   * 包括：灵感被使用次数、创建时间、AI 生成水印
   * @returns
   */
  const renderExtraInfo = () => {
    return (
      <div className="flex flex-wrap items-center md:mt-1 [&>span.hidden+span]:before:content-none [&>span:not(:first-child)]:before:mx-2 [&>span:not(:first-child)]:before:inline-block [&>span:not(:first-child)]:before:h-[10px] [&>span:not(:first-child)]:before:w-[1px] [&>span:not(:first-child)]:before:bg-system-white/20 [&>span:not(:first-child)]:before:content-['']">
        {miniVideo.info?.created_at && (
          <span className="text-system-white/50 text-xs">
            {format.dateTime(date, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              timeZone,
            })}
          </span>
        )}
        {SITE === 'china' && <span className="text-system-white/50 text-xs">内容由 AI 生成</span>}
      </div>
    );
  };

  /**
   * 在信息栏最底部展示的通用操作按钮
   * 包括：收藏、分享、更多、点赞
   * @returns
   */
  const renderCommonOperations = () => {
    const { react_info } = miniVideo.info || {};

    return (
      <div className="flex h-10 items-center gap-2 md:h-12 md:gap-3">
        {handleCollect && (
          <CollectBtn
            key={miniVideo.info?.id}
            id={miniVideo.info?.id || ''}
            tabType={CommunityEnum.MiniVideo}
            itemCollect={miniVideo.info?.react_info?.collected ?? false}
            itemCollectCount={miniVideo.info?.react_info?.collect_count ?? 0}
            handleCollect={handleCollect}
            handleUnCollect={handleUnCollect}
            hideCount={true}
            className="flex aspect-square size-10 h-full cursor-pointer items-center justify-center rounded-10 bg-white/10 text-lg backdrop-blur-lg hover:bg-system-white/20 md:size-12 md:rounded-12"
            iconClassName="size-5 md:size-6"
            traceId={miniVideo.traceId || ''}
          />
        )}
        <CommunityShareBtn id={miniVideo.info?.id || ''} type={CommunityEnum.MiniVideo} />
        {SITE === 'china' && (
          <EllipsisDropdownMenu
            className="aspect-square size-10 h-full rounded-10 bg-white/10 text-lg backdrop-blur-lg hover:bg-system-white/20 md:size-12 md:rounded-12 md:text-2xl"
            actions={[{ action: EllipsisActionTypeEnum.InspirationReport, icon: <BaseLineFeedback /> }]}
            onSelectedAction={handleActionDialog}
          />
        )}
        {react_info && (
          <LikeButton
            iconClassName="md:size-6"
            className="h-full flex-1 rounded-10 bg-white/10 text-lg backdrop-blur-lg hover:bg-system-white/20 md:size-12 md:rounded-12"
            id={miniVideo.info?.id || ''}
            itemVote={react_info?.voted || false}
            tabType={CommunityEnum.MiniVideo}
            itemVoteCount={react_info?.vote_count || 0}
            handleVote={handleVote}
            handleUnVote={handleUnVote}
            traceId={miniVideo.traceId || ''}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <div
        className="absolute inset-0 z-[-1] bg-black/60 bg-center bg-cover bg-no-repeat blur-[150px] md:hidden"
        style={{
          backgroundImage: `url('${miniVideo.info?.creation?.cover_uri}')`,
        }}
      >
        <div className="absolute inset-0 z-[-1] size-full bg-black/60" />
      </div>

      <div className="relative flex size-full flex-1 md:flex-col md:overflow-y-auto md:overflow-x-hidden">
        {renderMediaArea()}

        {/* 右侧信息栏 */}
        <div className="relative my-8 flex flex-col border-system-white10 border-l pr-[30px] pl-5 md:my-0 md:flex-1 md:border-0 md:px-4 md:pb-6">
          {renderInfo()}
        </div>
      </div>
    </>
  );
};
